import type { Dayjs } from 'dayjs';
import type { RoomOffsetHierarchy } from 'lumen/models/recommendation-strategy';
import { PMS_SETTINGS_OFFSET_TYPES } from 'lumen/utils/constants/pricing-assistant-constants';
import { overlaps } from 'lumen/utils/dates/moment-helpers';
import safeMultiply from 'lumen/utils/numeric/safe-multiply';

// A minimal seasonal-strategy shape, satisfied by RecommendationStrategy, so the
// overlap helpers stay pure and don't depend on EmberData.
export type SeasonalStrategyDateRange = {
  seasonEndDt: Dayjs | null;
  seasonStartDt: Dayjs | null;
};

// A single room's offset, lifted out of the nested hierarchy into a flat list.
export type FlatRoomOffset = {
  offset: RoomOffsetHierarchy['offset'];
  roomCode: string;
};

// Relative room offsets are stored as fractions (0.1) but shown as percentages
// (10). Absolute offsets pass through unchanged.
export function offsetToDisplayValue(
  value: number,
  offsetType: string,
): number {
  if (offsetType !== PMS_SETTINGS_OFFSET_TYPES.RELATIVE) {
    return value;
  }

  return safeMultiply(value, 100) ?? value;
}

// Inverse of offsetToDisplayValue: turns a displayed percentage (10) back into
// the stored fraction (0.1). Absolute offsets pass through unchanged.
export function offsetToStoredValue(value: number, offsetType: string): number {
  if (offsetType !== PMS_SETTINGS_OFFSET_TYPES.RELATIVE) {
    return value;
  }

  return safeMultiply(value, 1 / 100) ?? value;
}

/*
 * Flattens a room-offset hierarchy into a list of its non-root rooms. Root rooms
 * are the base rooms and hold no offset of their own; only their descendants
 * carry an offset relative to the base — which is what the flat seasonal editor
 * edits. (The feature only supports a leading-room + children hierarchy, so in
 * practice these are the leading room's direct children.)
 */
export function flattenRoomOffsets(
  hierarchy: Record<string, RoomOffsetHierarchy>,
): FlatRoomOffset[] {
  const collectDescendants = (node: RoomOffsetHierarchy): FlatRoomOffset[] =>
    Object.entries(node.children).flatMap(([roomCode, child]) => [
      { offset: child.offset, roomCode },
      ...collectDescendants(child),
    ]);

  return Object.values(hierarchy).flatMap(collectDescendants);
}

// Returns the seasonal strategies whose date range overlaps [start, end].
// Comparison is inclusive by day, so strategies that touch on the same day still
// conflict. Strategies without a full date range can't overlap and are skipped.
// Callers should exclude the strategy being edited from `seasonalStrategies`.
export function getOverlappingSeasonalStrategies<
  T extends SeasonalStrategyDateRange,
>(start: Dayjs | null, end: Dayjs | null, seasonalStrategies: T[]): T[] {
  if (!start || !end) {
    return [];
  }

  return seasonalStrategies.filter(({ seasonEndDt, seasonStartDt }) => {
    if (!seasonStartDt || !seasonEndDt) {
      return false;
    }

    return overlaps(start, end, seasonStartDt, seasonEndDt);
  });
}
